<template>
  <CredentialsSniffer
    :admin-urls="adminUrls"
    :timeoutMs="20000"
    @credentialsFound="handleCredentialsFound"
    @noCredentialsFound="handleNoCredentialsFound"
  ></CredentialsSniffer>
  <transition name="toast">
    <Toast v-if="errorMessage" :message="errorMessage" :variant="toastVariant" />
  </transition>
  <metainfo>
    <template v-slot:title="{ content }">{{ content ? `${content} | iCIMS Recorder` : 'iCIMS Recorder' }}</template>
  </metainfo>
  <Modal v-if="showModal" :title="modalTitle.title" :sub-title="modalTitle.subtitle"></Modal>
  <Loader v-if="isLoading" :loading-term-value="loadingTermValue" />
  <Navbar v-if="!isIframed" />
  <router-view />
  <Footer v-if="!isIframed" />
</template>

<script lang="ts">
/// <reference types="mixpanel" />
import Toast from '@/components/Toast.vue';
import Navbar from '@/components/Navbar.vue';
import Footer from '@/components/Footer.vue';
import Loader from '@/components/Spinner/Loader.vue';
import CredentialsSniffer from '@/components/CredentialsSniffer.vue';
import { useMeta } from 'vue-meta';
import { computed, Ref } from 'vue';
import { useStore } from 'vuex';
import { ModalTitleValues, ModalVariant, ToastVariant } from '@/store/ui/types';
import Modal from '@/components/Modal.vue';
import { InitialQueryParams, SetupReturnType } from '@/types/app';
import type { Client, globalRegions, Question } from 'altru-types';
import { Answer, AppFreeAnswerParameters } from 'altru-types';
import loadNewRelicScript from '@/utils/loadNewRelicScript';
import showErrorView from '@/utils/showErrorView';
import { useI18n } from 'vue-i18n';
import { Credentials } from '@/store/auth/types';
import { sendRecordingReadyMessage } from '@/iframe-messaging';
import { AltruApiResponse } from 'altru-types/@types/generic-response';
import { AxiosResponse } from 'axios';
import { getPrioritizedAdminOrigins } from '@/utils/adminOrigins';
import { RouteNames } from '@/router';
import { useRoute, useRouter } from 'vue-router';

function isAnswer(answer: Answer | AxiosResponse<AltruApiResponse<Answer>>): answer is Answer {
  return (answer as Answer).id !== undefined;
}

export default {
  name: 'App',
  components: {
    Loader,
    Navbar,
    Footer,
    Modal,
    Toast,
    CredentialsSniffer,
  },

  setup(): SetupReturnType {
    const { getters, commit, dispatch } = useStore();
    const { t } = useI18n();
    const { query } = useRoute();
    const { push } = useRouter();
    const isLoading: Ref<boolean> = computed(() => getters['ui/isLoading']);
    const loadingTermValue: Ref<string> = computed(() => getters['ui/loadingTermValue']);
    const showModal: Ref<boolean> = computed(() => getters['ui/showModal']);
    const modalTitle: Ref<ModalTitleValues> = computed(() => getters['ui/modalTitle']);
    const modalType: Ref<ModalVariant> = computed(() => getters['ui/modalVariant']);
    const errorMessage: Ref<string> = computed(() => getters['ui/errorMessageValue']);
    const toastVariant: Ref<ToastVariant> = computed(() => getters['ui/toastVariant']);
    const questionInfo: Ref<Question> = computed(() => getters['questions/questionInfo']);
    const workspaceInfo: Ref<Client | null> = computed(() => getters['clients/workspaceInfo']);
    const payloadValue: Ref<string | null> = computed(() => getters['auth/payloadValue']);
    const isIframed: Ref<boolean> = computed(() => getters['status/isIframed']);
    const adminUrls: Ref<string[]> = computed(() => getPrioritizedAdminOrigins());
    const originalUrl: Ref<string> = computed(() => getters['status/originalUrl']);

    /* Save original url */
    commit('status/storeOriginalUrl', window.location.href);

    const termsAccepted: Ref<boolean> = computed(() => getters['clients/workspaceTermsAcceptedAt']);
    const initialRedirectDone: Ref<boolean> = computed(() => getters['status/initialRedirectDone']);

    if (!initialRedirectDone.value && !termsAccepted.value) {
      // Set initial redirect value to false so this redirect doesn't happen again
      commit('status/setInitialRedirectDone', true);
      push({
        name: RouteNames.Home,
        query,
      });
    }

    // Set app usage ASAP (standalone/iframed)
    commit('status/setIsInIframe', window.self !== window.top);

    dispatch('ui/toggleLoading', { value: true });

    /* Dynamically load New Relic script based on active environment */
    loadNewRelicScript();
    /* Initialize Mixpanel */
    const mixpanelUrls: Record<'eu' | 'us', string> | null = process.env.VUE_APP_USAGE_URLS
      ? JSON.parse(process.env.VUE_APP_USAGE_URLS)
      : null;
    const mixpanels = Object.entries(mixpanelUrls || {}).reduce(
      (acc, [globalRegion, url]) => ({
        ...acc,
        ...(process.env.VUE_APP_MIXPANEL_TOKEN && mixpanelUrls
          ? {
              [globalRegion]: mixpanel.init(
                process.env.VUE_APP_MIXPANEL_TOKEN,
                {
                  secure_cookie: true,
                  ip: false,
                  api_host: url,
                  persistence: 'localStorage',
                },
                globalRegion,
              ),
            }
          : {}),
      }),
      {
        us: null,
        eu: null,
      } as {
        us: Mixpanel | null;
        eu: Mixpanel | null;
      },
    );

    const mixpanelRegisterVisit = (
      globalRegion: globalRegions | null | undefined,
      questionId: number,
      clientId?: number,
    ) => {
      const isTrackable = (gr: globalRegions | null | undefined): gr is 'eu' | 'us' =>
        ['us', 'eu'].includes(gr as string);

      const mp = isTrackable(globalRegion) && mixpanels[globalRegion];
      if (!mp) {
        console.error('Failed to send usage data %o', { globalRegion, questionId, clientId });
        return;
      }
      /* Register common event properties */
      mp.register({
        question_id: questionId,
        client_id: clientId,
      });

      /* track load event */
      mp.track('app-free-visit');
    };

    // APP initialization when using as standalone
    const standaloneAppInitialization = async (headersObject: Credentials | Record<string, never>) => {
      const queryParams = Object.fromEntries(new URL(originalUrl.value).searchParams) as InitialQueryParams;
      const {
        question_id: questionId,
        client_id: clientId,
        global_region: globalRegion,
        payload,
      }: InitialQueryParams = queryParams;
      mixpanelRegisterVisit(globalRegion, +questionId, +clientId);

      // Check that all query params are set or redirect to error view
      if (!questionId || !clientId || !payload) {
        const missingParams = [
          ...(!questionId ? ['Question ID'] : []),
          ...(!clientId ? ['Client ID'] : []),
          ...(!payload ? ['Payload'] : []),
        ].join(', ');
        await dispatch('ui/toggleLoading', { value: false });
        return showErrorView(t('MISSING QUERY PARAMETERS'), t('MISSING PARAMETERS: {0}', [missingParams]));
      }

      /* Set question info from question_id query param */
      if (questionInfo.value.id === -1 && questionId) {
        dispatch('questions/getQuestion', { questionId, region: workspaceInfo.value?.region });
      }

      /* Set workspace info from client_id query param */
      if (workspaceInfo.value === null && clientId) {
        if (payload) {
          await dispatch('clients/getWorkspace', { clientId, payload });
        } else {
          // At least we always have the id available from the URL
          commit('clients/setWorkspaceInfo', { id: clientId });
        }
      }

      /* Set payload info from payload query param */
      if (payloadValue.value === null) {
        dispatch('auth/storePayloadValue', payload);
      }

      // ME object only makes sense when we use admin credentials
      if (Object.keys(headersObject).length > 0) {
        dispatch('auth/initCredentials', { headersObject, queryParams });
      }

      await dispatch('ui/toggleLoading', { value: false });
    };

    // APP initialization when iframed
    const iframedAppInitialization = async (headersObject: Credentials | Record<string, never>) => {
      const isHeadersObjectMessage =
        Object.prototype.hasOwnProperty.call(headersObject, 'access-token') &&
        Object.prototype.hasOwnProperty.call(headersObject, 'client') &&
        Object.prototype.hasOwnProperty.call(headersObject, 'uid');

      // No headers object, redirect to error view
      if (!isHeadersObjectMessage) {
        await dispatch('ui/toggleLoading', { value: false });
        return await showErrorView(t('NO ADMIN CREDENTIALS'), t('LOG IN AND TRY AGAIN'));
      }

      const queryParams = Object.fromEntries(
        new URL(originalUrl.value).searchParams,
      ) as Partial<AppFreeAnswerParameters>;
      const { answer_id, source, global_region } = queryParams;

      // Set iframe source on store
      commit('status/setIframeSource', source);

      // Check that all query params are set or redirect to error view
      if (!answer_id || !source) {
        const missingParams = [...(!answer_id ? ['Answer ID'] : []), ...(!source ? ['Source'] : [])].join(', ');
        await dispatch('ui/toggleLoading', { value: false });
        return showErrorView(t('MISSING QUERY PARAMETERS'), t('MISSING PARAMETERS: {0}', [missingParams]));
      } else {
        // Retrieve answer using "answer_id" query param
        const answer: Answer = await dispatch(
          'answers/getOriginalAnswer',
          {
            answerId: answer_id,
            authHeaders: headersObject,
            region: global_region,
          },
          { root: true },
        );

        // Stop execution if we get an error while trying to retrieve the answer
        // The error feedback to the user is done by the interceptors on the file "src/store/api.ts"
        if (!isAnswer(answer) && (answer as unknown as AxiosResponse<AltruApiResponse<Answer>>).status !== 200) {
          await dispatch('ui/toggleLoading', { value: false });
          return;
        }

        const { client_id: clientId, question_id: questionId } = answer;
        // Retrieve and set client information using the answer object
        const workspace: Client = await dispatch(
          'clients/getWorkspaceWithCredentials',
          {
            clientId: clientId,
            authHeaders: headersObject,
            region: global_region,
          },
          { root: true },
        );
        // Retrieve and set question information using the answer object
        await dispatch(
          'questions/getQuestion',
          {
            questionId: questionId,
            region: workspace.global_region,
          },
          { root: true },
        );

        mixpanelRegisterVisit(workspace.global_region, questionId, clientId);

        // Set credentials and retrieve "me" object
        await dispatch('auth/initCredentialsInIframe', { headersObject });

        await dispatch('ui/toggleLoading', { value: false });

        sendRecordingReadyMessage({
          kind: 'answer recording ready',
          answerId: +answer_id,
        });
      }
    };

    const handleCredentialsFound = (credentials: Credentials): void => {
      if (isIframed.value) {
        iframedAppInitialization(credentials);
      } else {
        standaloneAppInitialization(credentials);
      }
    };

    const handleNoCredentialsFound = (): void => {
      if (isIframed.value) {
        iframedAppInitialization({});
      } else {
        standaloneAppInitialization({});
      }
    };

    const meta = useMeta({
      title: '',
      htmlAttrs: { lang: 'en', amp: true },
    });

    return {
      isLoading,
      loadingTermValue,
      meta,
      showModal,
      modalTitle,
      modalType,
      errorMessage,
      toastVariant,
      isIframed,
      adminUrls,
      handleCredentialsFound,
      handleNoCredentialsFound,
    };
  },
};
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css?family=Poppins');
</style>
